import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useState, useContext, useEffect, useCallback } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useCreateIdempotencyKeys } from "../../utils/idempotency";
import {
  SAVE_SERVICE,
  buildSaveServiceRequest,
  buildPartsNeededTicket,
  saveServiceRequestFromOffline,
} from "./saveServiceRequest";
import { useUserList } from "../../contexts/UserListContext";
import { useAuth } from "../../contexts/AuthContext";
import ServiceDefinitionDropDown from "../../dropdowns/ServiceDefinitionDropDown";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import InspectorStatusSelector from "./InspectorStatusSelector";
import {
  isInspections,
  isNumericCollectionType,
  isDecimalCollectionType,
  normalizeCollectionValue,
  collectionValueFromPart,
  hasCollectionUnitLabel,
  getCollectionUnitLabel,
} from "./serviceItemHelpers";
import styled from "styled-components";
import {
  saveOfflineService,
  getUnsyncedServices,
  markServiceSynced,
  isOnline,
  getUnsyncedCount,
} from "../../utils/offlineStorage";

const ServiceItemsContainer = styled.div`
  margin-top: 20px;
`;

const ServiceItemsTitle = styled.h3`
  margin-bottom: 15px;
`;

const SectionCard = styled.div.attrs({ className: "service-section-card" })`
  margin-bottom: 20px;
  padding: 15px;
`;

const SectionHeader = styled.h4`
  margin-top: 0;
  margin-bottom: 12px;
`;

const ItemContainer = styled.div.attrs({ className: "service-item-container service-item-row" })`
  margin-bottom: 15px;
  padding-bottom: ${props => props.$isLast ? '0' : '15px'};
  border-bottom: ${props => props.$isLast ? 'none' : '1px solid var(--card-border)'};
`;

const ItemNameContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const ItemName = styled.span`
  flex: 1;
`;

const RequiredAsterisk = styled.span`
  color: #dc3545;
  margin-left: 4px;
`;

const ItemInputContainer = styled.div`
  margin-top: 8px;
`;

const ItemLabel = styled.label`
  display: block;
  margin-bottom: 4px;
`;

const ItemInput = styled.input.attrs({ className: "service-item-input" })`
  width: 100%;
  padding: 6px 10px;
  border-radius: 4px;
`;

// GraphQL queries and mutations (same as ServiceEdit)
const entityQuery = gql`
  query serviceList($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      edges {
        id
        service_definition {
          id
          name
        }
        crane {
          id
          name
        }
        user_id
      }
      totalCount
    }
  }
`;

const serviceItemsQuery = gql`
  query serviceItemsByDefinition($request: serviceDefinition2ServiceItem_list_request) {
    serviceDefinition2ServiceItem(serviceDefinition2ServiceItem_list_request: $request) {
      edges {
        id
        service_item_id
        service_item {
          id
          name
          sequence
          required
          service_section {
            id
            name
          }
          service_group {
            id
            name
          }
          service_type {
            id
            name
          }
          service_unit {
            id
            name
          }
        }
      }
    }
  }
`;

const servicePartsQuery = gql`
  query serviceParts($request: service_part_list_request) {
    service_part(service_part_list_request: $request) {
      edges {
        id
        service_item_id
        part_no
        description
        quantity
      }
    }
  }
`;

const serviceValuesQuery = gql`
  query serviceValues($request: service_value_list_request) {
    service_value(service_value_list_request: $request) {
      edges {
        id
        status
        service_item_id
        service_id
        description
      }
    }
  }
`;

const craneNameQuery = gql`
  query craneNameLookupTech($crane_list_request: crane_list_request) {
    crane(crane_list_request: $crane_list_request) {
      edges { id name }
    }
  }
`;

const assignedServiceDefinitionsQuery = gql`
  query assignedServiceDefinitions($serviceDefinition2Crane_list_request: serviceDefinition2Crane_list_request) {
    serviceDefinition2Crane(serviceDefinition2Crane_list_request: $serviceDefinition2Crane_list_request) {
      edges {
        service_definition_id
      }
    }
  }
`;

export default function TechnicianServiceInterface({ id: entityId, action, navigate, options = {}, readOnly = false }) {
  const isEdit = action === "edit";
  const canEnterItemValues = !readOnly && !isEdit;
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const userList = useUserList();

  const [serviceDefinition, setServiceDefinition] = useState('');
  const [crane, setCrane] = useState(options.crane_id || '');
  const [craneName, setCraneName] = useState('');
  const [technicianId, setTechnicianId] = useState();
  const [technicianName, setTechnicianName] = useState(null);
  const [serviceItems, setServiceItems] = useState([]);
  const [serviceItemStates, setServiceItemStates] = useState({});
  const [groupedItems, setGroupedItems] = useState({ inspections: [], collections: [] });
  const [invalidReasons, setInvalidReasons] = useState([]);
  const [assignedDefinitionIds, setAssignedDefinitionIds] = useState([]);
  const [serviceComment, setServiceComment] = useState("");
  const [onlineStatus, setOnlineStatus] = useState(isOnline());
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setOnlineStatus(true);
      addNotification("good", "Connection restored. Syncing offline data...");
      syncOfflineData();
    };

    const handleOffline = () => {
      setOnlineStatus(false);
      addNotification("bad", "You are offline. Data will be saved locally and synced when connection is restored.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    setOnlineStatus(isOnline());
    setUnsyncedCount(getUnsyncedCount());

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch assigned service definitions
  useQuery(assignedServiceDefinitionsQuery, {
    variables: {
      serviceDefinition2Crane_list_request: {
        filters: [{ field: "crane_id", operator: "=", value: String(crane) }],
      },
    },
    skip: !crane || isEdit,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const edges = data?.serviceDefinition2Crane?.edges ?? [];
      const definitionIds = edges
        .map((e) => e?.service_definition_id)
        .filter((id) => id != null)
        .map((id) => Number(id));
      setAssignedDefinitionIds(Array.from(new Set(definitionIds)));
    },
    onError: (error) => {
      console.error("Error fetching assigned service definitions:", error);
    },
  });

  // On crane change (add mode), clear dependent selection so the dropdown is always consistent.
  useEffect(() => {
    if (isEdit) return;
    setAssignedDefinitionIds([]);
    setServiceDefinition("");
  }, [crane, isEdit]);

  // If the selected service definition is no longer valid for this crane, clear it.
  useEffect(() => {
    if (isEdit) return;
    if (!serviceDefinition) return;
    const idNum = Number(serviceDefinition);
    if (assignedDefinitionIds.length > 0 && !assignedDefinitionIds.includes(idNum)) {
      setServiceDefinition("");
    }
  }, [assignedDefinitionIds, serviceDefinition, isEdit]);

  const variables = {
    service_list_request: {
      filters: [
        { field: "id", operator: "=", value: String(entityId ?? 0) },
      ],
    },
  };

  const [fetchCraneName] = useLazyQuery(craneNameQuery, {
    fetchPolicy: "network-only",
    onCompleted: (data) => setCraneName(data?.crane?.edges?.[0]?.name || ''),
  });

  useEffect(() => {
    if (crane) {
      fetchCraneName({
        variables: {
          crane_list_request: {
            filters: [{ field: 'id', operator: '=', value: String(crane) }],
          },
        },
      });
    } else {
      setCraneName('');
    }
  }, [crane]);

  const { loading: loadingEntity, refetch: refetchEntity } = useQuery(entityQuery, {
    variables,
    skip: !isEdit,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const service = data?.service?.edges?.[0];
      if (service) {
        const definitionId = service.service_definition?.id || "";
        setServiceDefinition(definitionId);
        setCrane(options.crane_id ? options.crane_id : (service.crane?.id || ""));
        setCraneName(service.crane?.name || '');
        setTechnicianId(service.user_id != null ? String(service.user_id) : "");
      }
    },
    onError: (error) => console.error(error),
  });

  useEffect(() => {
    if (serviceDefinition) {
      fetchServiceItems({
        variables: {
          request: {
            filters: [
              { field: "service_definition_id", operator: "=", value: String(serviceDefinition) }
            ]
          }
        }
      });
    } else {
      setServiceItems([]);
    }
  }, [serviceDefinition, entityId]);

  useEffect(() => {
    if (technicianId && userList?.length) {
      const found = userList.find(u => u._id === technicianId);
      setTechnicianName(found?.name || "—");
    }
  }, [userList, technicianId]);

  useEffect(() => {
    if (!isEdit && user) {
      setTechnicianId(user._id);
      setTechnicianName(user.name);
    }
  }, [isEdit, user]);

  const [fetchServiceItems] = useLazyQuery(serviceItemsQuery, {
    onCompleted: (data) => {
      const items = data?.serviceDefinition2ServiceItem?.edges || [];
      setServiceItems(items.map(e => e.service_item));
    },
    onError: (err) => {
      console.error(err);
      addNotification("bad", "Failed to load service items");
    }
  });

  const [fetchServiceParts, { data: servicePartsData }] = useLazyQuery(servicePartsQuery, {
    fetchPolicy: "network-only",
    onError: (err) => {
      console.error(err);
      addNotification("bad", "Failed to load service parts");
    }
  });

  const [fetchServiceValues, { data: serviceValuesData }] = useLazyQuery(serviceValuesQuery, {
    fetchPolicy: "network-only",
    onError: (err) => {
      console.error(err);
      addNotification("bad", "Failed to load service values");
    }
  });

  const updateItemState = (id, updates) => {
    setServiceItemStates(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...updates,
      },
    }));
  };

  useEffect(() => {
    if (!serviceItems.length) return;

    const newItemStates = {};
    const inspections = {};
    const collections = {};

    const savedParts = servicePartsData?.service_part?.edges || [];
    const savedValues = serviceValuesData?.service_value?.edges || [];

    const serviceLevelValue = savedValues.find(v => !v.service_item_id);
    if (serviceLevelValue) {
      setServiceComment(serviceLevelValue.description || "");
    }

    serviceItems.forEach(item => {
      const sectionId = item.service_section?.id || "no-section";
      const savedPart = savedParts.find(p => Number(p.service_item_id) === Number(item.id));
      const savedValue = savedValues.find(v => Number(v.service_item_id) === Number(item.id));
      const statusInt = savedValue?.status;
      const status = (statusInt === 1 ? "action" : statusInt === 2 ? "ok" : "");

      newItemStates[item.id] = {
        status: isInspections(item) ? status : "",
        quantity: savedPart?.quantity != null ? String(savedPart.quantity) : "",
        partNo: savedPart?.part_no || "",
        comment: savedValue?.description || "",
        unit: isInspections(item) ? (savedPart?.quantity != null ? String(savedPart.quantity) : "") : collectionValueFromPart(savedPart),
      };

      if (isInspections(item)) {
        if (!inspections[sectionId]) {
          inspections[sectionId] = {
            section: item.service_section,
            items: [],
            groupId: item.service_group?.id || 0
          };
        }
        inspections[sectionId].items.push(item);
      } else {
        if (!collections[sectionId]) {
          collections[sectionId] = {
            section: item.service_section,
            items: [],
            groupId: item.service_group?.id || 0
          };
        }
        collections[sectionId].items.push(item);
      }
    });

    Object.values(inspections).forEach(group => {
      group.items.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    });
    Object.values(collections).forEach(group => {
      group.items.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    });

    setServiceItemStates(newItemStates);
    setGroupedItems({ inspections, collections });
  }, [serviceItems, servicePartsData, serviceValuesData, entityId]);

  const validateForm = () => {
    const reasons = [];
    if (!serviceDefinition) reasons.push("Service Definition is required.");
    if (!crane) reasons.push("Crane is required.");
    if (!technicianId?.trim()) reasons.push("Technician is required.");

    serviceItems.forEach(item => {
      const state = serviceItemStates[item.id];
      if (!state) return;

      if (isInspections(item)) {
        if (item.required && !state.status) {
          reasons.push(`Inspection "${item.name}" requires a status (Action or OK).`);
        }
        if (state.status === "action" && !isEdit) {
          if (!state.quantity?.trim() && !state.partNo?.trim()) {
            reasons.push(`Inspection "${item.name}" requires Quantity or Part Number.`);
          }
        }
      }

      if (!isInspections(item) && item.required) {
        if (state.unit === null || state.unit === "" || (isNumericCollectionType(item) && isNaN(parseFloat(state.unit)))) {
          reasons.push(`Collection "${item.name}" requires a value.`);
        }
      }
    });

    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const [mutateSaveService] = useMutation(SAVE_SERVICE);
  const [isSaving, setIsSaving] = useState(false);
  const createKeys = useCreateIdempotencyKeys();

  const buildCurrentSaveRequest = () => {
    const fingerprint = JSON.stringify({
      service_definition_id: Number(serviceDefinition),
      crane_id: Number(crane),
      user_id: Number(technicianId),
    });
    return buildSaveServiceRequest({
      serviceId: entityId || undefined,
      serviceDefinitionId: serviceDefinition,
      craneId: crane,
      userId: technicianId || user?._id,
      idempotencyKey: entityId ? undefined : createKeys.keyFor("service", fingerprint),
      subscribe: !entityId,
      serviceItems,
      serviceItemStates,
      serviceComment,
      savedParts: servicePartsData?.service_part?.edges || [],
      savedValues: serviceValuesData?.service_value?.edges || [],
      partsNeededTicket: entityId
        ? null
        : buildPartsNeededTicket({
            serviceItems,
            serviceItemStates,
            crane,
            craneName,
            technicianName,
          }),
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const request = buildCurrentSaveRequest();
    const offlinePayload = {
      service_id: entityId ? Number(entityId) : null,
      request,
      service_definition_id: Number(serviceDefinition),
      crane_id: Number(crane),
      user_id: technicianId,
      idempotency_key: request.idempotency_key,
      serviceComment,
      serviceItemStates,
      serviceItems: serviceItems.map(item => ({
        id: item.id,
        name: item.name,
        isInspection: isInspections(item),
        service_type: item.service_type,
      })),
    };

    if (!onlineStatus) {
      try {
        saveOfflineService(offlinePayload);
        setUnsyncedCount(getUnsyncedCount());
        addNotification("good", "Service saved offline. It will be synced when connection is restored.");
        if (!entityId) {
          setServiceDefinition('');
          setServiceItemStates({});
          setGroupedItems({ inspections: [], collections: [] });
        }
      } catch (error) {
        console.error("Error saving offline:", error);
        addNotification("bad", "Failed to save offline. Please try again.");
      }
      return;
    }

    setIsSaving(true);
    try {
      const res = await mutateSaveService({ variables: { request } });
      const serviceId = res.data?.save_service?.id;
      if (serviceId) {
        addNotification("good", "Service saved successfully");
        if (res.data?.save_service?.ticket_id) {
          addNotification("good", "Parts Needed - Service ticket created.");
        }
        navigate({ action: "edit", id: serviceId, push: false });
        refetchEntity();
      }
    } catch (err) {
      console.error(err);
      try {
        saveOfflineService(offlinePayload);
        setUnsyncedCount(getUnsyncedCount());
        addNotification("bad", "Save failed. Data saved offline and will be synced later.");
      } catch (offlineError) {
        console.error("Error saving offline:", offlineError);
        addNotification("bad", "Something went wrong");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const syncOfflineData = useCallback(async () => {
    if (!onlineStatus || isSyncing) return;

    const unsynced = getUnsyncedServices();
    if (unsynced.length === 0) {
      setUnsyncedCount(0);
      return;
    }

    setIsSyncing(true);
    let successCount = 0;
    let failCount = 0;

    for (const offlineService of unsynced) {
      try {
        const request = saveServiceRequestFromOffline(offlineService);
        const res = await mutateSaveService({ variables: { request } });
        if (res.data?.save_service?.id) {
          markServiceSynced(offlineService.key);
          successCount++;
        }
      } catch (error) {
        console.error("Error syncing offline service:", error);
        failCount++;
      }
    }

    setIsSyncing(false);
    setUnsyncedCount(getUnsyncedCount());

    if (successCount > 0) {
      addNotification("good", `Successfully synced ${successCount} service(s)`);
    }
    if (failCount > 0) {
      addNotification("bad", `Failed to sync ${failCount} service(s). They will be retried later.`);
    }
  }, [onlineStatus, isSyncing, mutateSaveService, addNotification]);

  useEffect(() => {
    if (onlineStatus && unsyncedCount > 0 && !isSyncing) {
      syncOfflineData();
    }
  }, [onlineStatus, unsyncedCount, syncOfflineData, isSyncing]);

  useEffect(() => { refetchEntity(); }, [entityId]);

  useEffect(() => {
    if (isEdit && entityId) {
      setServiceItems([]);
      setServiceItemStates({});
      setGroupedItems({ inspections: [], collections: [] });
      fetchServiceParts({
        variables: {
          request: { filters: [{ field: "service_id", operator: "=", value: String(entityId) }] }
        }
      });
      fetchServiceValues({
        variables: {
          request: { filters: [{ field: "service_id", operator: "=", value: String(entityId) }] }
        }
      });
    }
  }, [isEdit, entityId]);

  // Update unsynced count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setUnsyncedCount(getUnsyncedCount());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loadingEntity) return <LoadingView />;

  return (
    <>
      <div className="page-header">
        <span className="page-header-title">{ readOnly ? "Service Detail" : (isEdit ? "Edit" : "Add") + " Service Detail" }</span>
        { options.crane_id && (
          <div className="page-header-options">
            <button
              type="button"
              onClick={ () => navigate({ action: "list", id: null }) }
            >
              Back to list
            </button>
          </div>
        ) }
      </div>

      { (unsyncedCount > 0 || !onlineStatus) && (
        <div style={ {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginBottom: '16px',
          background: onlineStatus ? '#d4edda' : '#f8d7da',
          border: `1px solid ${onlineStatus ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '4px',
          fontSize: '14px',
          color: onlineStatus ? '#155724' : '#721c24'
        } }>
          <div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
            <div style={ {
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: onlineStatus ? '#28a745' : '#dc3545',
              animation: onlineStatus ? 'none' : 'pulse 2s infinite'
            } } />
            <span>{ onlineStatus ? "Online" : "Offline" }</span>
            { unsyncedCount > 0 && (
              <span style={ {
                background: '#ffc107',
                color: '#000',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                marginLeft: '8px'
              } }>
                { unsyncedCount } unsynced
              </span>
            ) }
          </div>
          { unsyncedCount > 0 && onlineStatus && (
            <button
              onClick={ syncOfflineData }
              disabled={ isSyncing }
              style={ {
                padding: '8px 16px',
                background: '#28a745',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: isSyncing ? 'not-allowed' : 'pointer',
                opacity: isSyncing ? 0.6 : 1
              } }
            >
              { isSyncing ? "Syncing..." : "Sync Now" }
            </button>
          ) }
        </div>
      ) }

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Crane:</label>
            <CraneDropDown
              selected={ crane }
              onSelect={ setCrane }
              searchEnabled
              multiple={ false }
              disabled={ readOnly || isEdit || !!options.crane_id }
            />
          </div>
          <div className="edit-property">
            <label>Section:</label>
            <ServiceDefinitionDropDown
              selected={ serviceDefinition }
              onSelect={ (id) => {
                setServiceDefinition(id);
              } }
              searchEnabled
              disabled={ readOnly || isEdit || !crane || assignedDefinitionIds.length === 0 }
              filters={
                crane && !isEdit
                  ? assignedDefinitionIds.length > 0
                    ? [
                        { field: "id", operator: "in", value: `(${assignedDefinitionIds.join(", ")})` },
                      ]
                    : [{ field: "id", operator: "in", value: "(-1)" }]
                  : []
              }
            />
          </div>
          <div className="edit-property">
            <label>Technician:</label>
            <div>{ technicianName || "—" }</div>
          </div>
        </div>

        { Object.keys(groupedItems.inspections).length > 0 && (
          <div className="add-edit" style={ { marginTop: '20px' } }>
            <h3 style={ { marginBottom: '15px' } }>Inspections</h3>
            { Object.values(groupedItems.inspections)
              .sort((a, b) => {
                if (a.groupId !== b.groupId) {
                  return (a.groupId || 0) - (b.groupId || 0);
                }
                return (a.section?.name || "").localeCompare(b.section?.name || "");
              })
              .map(({ section, items }) => (
                <div key={ section?.id || "no-section" } className="service-section-card" style={ { marginBottom: '20px', padding: '15px' } }>
                  <h4 style={ { marginTop: '0', marginBottom: '12px' } }>{ section?.name || "No Section" }</h4>
                  { items.map((item, itemIndex) => {
                    const state = serviceItemStates[item.id] || {};
                    return (
                      <div
                        key={ `inspection-${section?.id || "no-section"}-${item.id}-${itemIndex}` }
                        className={ `service-inspection-item${state.status ? ` service-inspection-item--${state.status}` : ""}` }
                      >
                        <div className="flex-center" style={ { marginBottom: '10px' } }>
                          <span style={ { flex: 1 } }>
                            { item.name }{ item.required === 1 && <span style={ { color: '#dc3545', marginLeft: '4px' } }>*</span> }
                          </span>
                          <InspectorStatusSelector
                            value={ state.status ?? "" }
                            onChange={ (newStatus) =>
                              isInspections(item) && updateItemState(item.id, { status: newStatus })
                            }
                            disabled={ readOnly || isEdit }
                          />
                        </div>
                        { state.status === "action" && (
                          <div className="edit-section service-action-panel" style={ { marginTop: '10px', padding: '12px', borderRadius: '4px' } }>
                            { !isEdit && (
                              <>
                                <div className="edit-property" style={ { marginBottom: '10px' } }>
                                  <label style={ { display: 'block', marginBottom: '4px' } }>Quantity:</label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={ state.quantity ?? "" }
                                    onChange={ e =>
                                      updateItemState(item.id, { quantity: e.target.value.replace(/\D/g, "").slice(0, 10) })
                                    }
                                    disabled={ readOnly }
                                    className="service-item-input"
                                    style={ { width: '100%', padding: '6px 10px', borderRadius: '4px' } }
                                  />
                                </div>
                                <div className="edit-property">
                                  <label style={ { display: 'block', marginBottom: '4px' } }>Part Number:</label>
                                  <input
                                    type="text"
                                    value={ state.partNo ?? "" }
                                    onChange={ e =>
                                      updateItemState(item.id, { partNo: e.target.value })
                                    }
                                    disabled={ readOnly }
                                    className="service-item-input"
                                    style={ { width: '100%', padding: '6px 10px', borderRadius: '4px' } }
                                  />
                                </div>
                              </>
                            ) }
                            <div className="edit-property" style={ { marginTop: '10px', marginBottom: 0 } }>
                              <label style={ { display: 'block', marginBottom: '4px' } }>Comment:</label>
                              <textarea
                                value={ state.comment ?? "" }
                                onChange={ e => updateItemState(item.id, { comment: e.target.value }) }
                                disabled={ readOnly || isEdit }
                                rows={ 3 }
                                className="service-item-input"
                                style={ { width: '100%', padding: '6px 10px', borderRadius: '4px' } }
                              />
                            </div>
                          </div>
                        ) }
                      </div>
                    );
                  }) }
                </div>
              )) }
          </div>
        ) }

        { Object.keys(groupedItems.collections).length > 0 && (
          <ServiceItemsContainer>
            <ServiceItemsTitle>Collections</ServiceItemsTitle>
            { Object.values(groupedItems.collections)
              .sort((a, b) => {
                if (a.groupId !== b.groupId) {
                  return (a.groupId || 0) - (b.groupId || 0);
                }
                return (a.section?.name || "").localeCompare(b.section?.name || "");
              })
              .map(({ section, items }) => (
                <SectionCard key={ section?.id || "no-section" }>
                  <SectionHeader>{ section?.name || "No Section" }</SectionHeader>
                  { items.map((item, index) => {
                    const state = serviceItemStates[item.id] || {};
                    const isLast = index === items.length - 1;
                    return (
                      <ItemContainer key={ `collection-${section?.id || "no-section"}-${item.id}-${index}` } $isLast={ isLast }>
                        <ItemNameContainer>
                          <ItemName>
                            { item.name }
                            { item.required === 1 && <RequiredAsterisk>*</RequiredAsterisk> }
                          </ItemName>
                        </ItemNameContainer>
                        <ItemInputContainer>
                          { hasCollectionUnitLabel(item) && (
                            <ItemLabel>{ getCollectionUnitLabel(item) }:</ItemLabel>
                          ) }
                          <ItemInput
                            type="text"
                            inputMode={ isNumericCollectionType(item) ? (isDecimalCollectionType(item) ? "decimal" : "numeric") : "text" }
                            value={ state.unit || "" }
                            onChange={ e =>
                              updateItemState(item.id, { unit: normalizeCollectionValue(item, e.target.value) })
                            }
                            disabled={ !canEnterItemValues }
                          />
                        </ItemInputContainer>
                      </ItemContainer>
                    );
                  }) }
                </SectionCard>
              )) }
          </ServiceItemsContainer>
        ) }

        <div className="edit-section">
          <div className="edit-property">
            <label>Service Comment:</label>
            <textarea
              value={ serviceComment }
              onChange={ e => setServiceComment(e.target.value) }
              disabled={ !canEnterItemValues }
              rows={ 3 }
              className="service-item-input"
              style={ { width: '100%', padding: '6px 10px', borderRadius: '4px' } }
            />
          </div>
        </div>

        { invalidReasons.length > 0 && !readOnly && <InvalidReasons reasons={ invalidReasons } /> }

        { !readOnly && (
          <div className="edit-section">
            <SaveButton
              onClick={ handleSave }
              saving={ isSaving }
              disabled={ !serviceDefinition || !crane || isEdit }
              style={ { marginBottom: '20px' } }
            >
              { onlineStatus ? "Save Service" : "Save Offline" }
            </SaveButton>
          </div>
        ) }
      </div>
    </>
  );
}
